import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { de } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { Task } from '@shared/types';
import { CenteredSpinner, ErrorState } from '@/components/ui';
import { cn } from '@/lib/format';
import { useTasks } from '@/lib/queries';

/**
 * Month calendar of everything with a due date, across all projects.
 *
 * Replaces the legacy "All Tasks Calendar", which was a Gantt chart of 5,000
 * rows — technically a timeline, practically unreadable.
 */
export function CalendarPage() {
  const [month, setMonth] = useState(() => startOfMonth(new Date()));

  // The grid spills into neighbouring months, so fetch a window wide enough to
  // fill the visible leading and trailing days.
  const rangeStart = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
  const rangeEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });

  const query = useTasks({
    dueAfter: new Date(rangeStart.getTime() - 86_400_000).toISOString(),
    dueBefore: new Date(rangeEnd.getTime() + 86_400_000).toISOString(),
    sort: 'due_date',
    perPage: 500,
  });

  const byDay = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const task of query.data?.items ?? []) {
      if (!task.dueDate) continue;
      const key = format(parseISO(task.dueDate), 'yyyy-MM-dd');
      const list = map.get(key);
      if (list) list.push(task);
      else map.set(key, [task]);
    }
    return map;
  }, [query.data]);

  const days = useMemo(
    () => eachDayOfInterval({ start: rangeStart, end: rangeEnd }),
    [rangeStart, rangeEnd],
  );

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-3 border-b line surface-card px-5 py-3">
        <h1 className="mr-auto text-base font-semibold text-strong">
          {format(month, 'MMMM yyyy', { locale: de })}
        </h1>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setMonth((m) => addMonths(m, -1))}
            className="grid size-8 place-items-center rounded-lg text-muted hover:bg-[var(--surface-hover)] hover:text-strong"
            aria-label="Vorheriger Monat"
          >
            <ChevronLeft className="size-4" />
          </button>
          <button
            onClick={() => setMonth(startOfMonth(new Date()))}
            className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-muted hover:bg-[var(--surface-hover)] hover:text-strong"
          >
            Heute
          </button>
          <button
            onClick={() => setMonth((m) => addMonths(m, 1))}
            className="grid size-8 place-items-center rounded-lg text-muted hover:bg-[var(--surface-hover)] hover:text-strong"
            aria-label="Nächster Monat"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
      </header>

      {query.isPending ? (
        <CenteredSpinner label="Kalender wird geladen…" />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <div className="min-h-0 flex-1 overflow-hidden p-3">
          {/*
            Grid stretches to fill the whole card: the header row is `auto`
            and the 5/6 week rows share the remaining height equally (1fr
            each). Previously each cell was min-h-24, so the whole calendar
            sat at ~600px total and the bottom of the viewport was empty.
            Cells now grow with the viewport and show more tasks before
            "+N weitere" kicks in.
          */}
          <div
            className="grid h-full grid-cols-7 gap-px overflow-hidden rounded-[var(--radius-card)] border line bg-[var(--line)]"
            style={{ gridTemplateRows: `auto repeat(${Math.ceil(days.length / 7)}, minmax(6rem, 1fr))` }}
          >
            {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((label) => (
              <div
                key={label}
                className="surface-raised px-2 py-1.5 text-center text-[11px] font-semibold uppercase text-muted"
              >
                {label}
              </div>
            ))}

            {days.map((day) => {
              const key = format(day, 'yyyy-MM-dd');
              const tasks = byDay.get(key) ?? [];
              const outside = !isSameMonth(day, month);
              // Show up to 8 tasks (was hard-coded 3); busy days can still
              // spill into "+N weitere" without dominating the row.
              const shown = Math.min(tasks.length, 8);
              return (
                <div
                  key={key}
                  className={cn(
                    'flex min-h-0 flex-col p-1.5',
                    outside ? 'surface-sunken' : 'surface-card',
                    // Weekends get a subtle tint so the working week reads first.
                    !outside && (day.getDay() === 0 || day.getDay() === 6) && 'surface-raised',
                  )}
                >
                  <div className="mb-1 flex items-center justify-between">
                    <span
                      className={cn(
                        'grid size-5 place-items-center rounded-full text-[11px] tabular-nums',
                        isToday(day)
                          ? 'bg-brand-500 font-semibold text-white'
                          : outside
                            ? 'text-faint'
                            : 'text-muted',
                      )}
                    >
                      {format(day, 'd')}
                    </span>
                    {tasks.length > shown && (
                      <span className="text-[10px] text-faint tabular-nums">{tasks.length}</span>
                    )}
                  </div>

                  <div className="min-h-0 flex-1 space-y-0.5 overflow-hidden">
                    {tasks.slice(0, shown).map((task) => (
                      <Link
                        key={task.id}
                        to="/tasks/$taskId"
                        params={{ taskId: task.id }}
                        title={task.title}
                        className={cn(
                          'block truncate rounded px-1 py-0.5 text-[10px] leading-tight transition-colors',
                          task.done
                            ? 'text-faint line-through hover:bg-[var(--surface-hover)]'
                            : 'bg-brand-50 text-brand-800 hover:bg-brand-100 dark:bg-brand-900/40 dark:text-brand-100 dark:hover:bg-brand-900/60',
                        )}
                      >
                        {task.title}
                      </Link>
                    ))}
                    {tasks.length > shown && (
                      <span className="block px-1 text-[10px] text-faint">
                        +{tasks.length - shown} weitere
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
